import { DateToIsoString } from '@mntn/api-contracts/_shared/date-to-iso-string'
import {
  ImageAttachmentFormSchema,
  ImageAttachmentResponseWithCroppedUrlSchema,
  ImageAttachmentWithRequiredCropSchema,
} from '@mntn/api-contracts/_shared/image-attachments'
import { PageResponseSchema } from '@mntn/api-contracts/_shared/page-response'
import { PaginationQuerySchema } from '@mntn/api-contracts/_shared/pagination'
import { ProgramGenreResponseSchema } from '@mntn/api-contracts/seller/v1/program-genres'
import { ProgramRatingResponseSchema } from '@mntn/api-contracts/seller/v1/program-ratings'
import { VideoAttachmentSchema } from '@mntn/api-contracts/seller/v1/video-attachments'
import { Actors as A, Tenants as T } from '@mntn/auth-types'
import {
  CoerceBooleanSchema,
  IDSchema,
  NaturalNumberSchema,
  RequiredIDsSchema,
  RequiredQueryIDsSchema,
} from '@mntn/zod'
import { z } from 'zod'

const ProgramFormatSchema = z.union([z.literal('show'), z.literal('movie'), z.literal('event'), z.literal('short')])

export const BaseProgramSchema = z.object({
  name: z.string().min(1, 'Name is required').max(64),
  description: z.string().min(1, 'Description is required'),
  artworkUrl: z.string().url({ message: 'Must be a valid URL' }),
  backgroundUrl: z.string().url({ message: 'Must be a valid URL' }),
  numberOfSeasons: z.number().int().nonnegative(),
  isPublished: z.boolean(),
  defaultCpm: NaturalNumberSchema.nullable(),
  networkId: IDSchema.positive('Please select a network'),
  programRatingId: IDSchema.positive('Please select a program rating'),
  programFormatId: IDSchema.positive('Please select a program format'),
})

export const ProgramSchema = BaseProgramSchema.extend({
  programId: IDSchema,
  publisherId: IDSchema,
  createTime: DateToIsoString,
  updateTime: DateToIsoString,
})

export const EmbeddingVectorSchema = z.array(z.number()).nullable().optional()

export const GetProgramsQuerySchema = z
  .object({
    search: z.string().optional(),
    exclude: z.string().optional(),
    order: z.union([z.literal('asc'), z.literal('desc')]).default('asc'),
    'order-by': z.union([z.literal('search'), z.literal('name'), z.literal('created')]).default('name'),
    'store-id': RequiredQueryIDsSchema.optional(),
    'program-id': RequiredQueryIDsSchema.optional(),
    'offering-id': RequiredQueryIDsSchema.optional(),
    'network-id': RequiredQueryIDsSchema.optional(),
    'pinned-id': RequiredQueryIDsSchema.optional(),
    'excluded-id': RequiredQueryIDsSchema.optional(),
    'program-format-id': RequiredQueryIDsSchema.optional(),
    'program-genre-id': RequiredQueryIDsSchema.optional(),
    'program-rating-id': RequiredQueryIDsSchema.optional(),
    'with-video': CoerceBooleanSchema.optional(),
    'include-embedding': CoerceBooleanSchema.default(false),
  })
  .merge(PaginationQuerySchema)

export const toGetProgramsDomainRequest = (query: z.output<typeof GetProgramsQuerySchema>) => ({
  query: {
    storeIds: query['store-id'],
    searchTerm: query.search,
    exclusionTerm: query.exclude,
    programIds: query['program-id'],
    offeringIds: query['offering-id'],
    withVideo: query['with-video'] ?? false,
    programGenreIds: query['program-genre-id'],
    programFormatIds: query['program-format-id'],
    programRatingIds: query['program-rating-id'],
    networkIds: query['network-id'],
    pinnedIds: query['pinned-id'],
    excludedIds: query['excluded-id'],
    includeEmbedding: query['include-embedding'],
  },
  sort: {
    order: query.order,
    orderBy: query['order-by'],
  },
  pagination: {
    limit: query.limit,
    offset: query.offset,
  },
})

export const GetProgramsResponseSchema = z.object({
  data: z.array(
    ProgramSchema.extend({
      network: z.object({
        name: z.string().min(1, 'Name is required'),
        description: z.string().min(1, 'Description is required'),
        logo: ImageAttachmentResponseWithCroppedUrlSchema,
        networkId: IDSchema,
      }),
      genres: z.array(ProgramGenreResponseSchema),
      videoAttachments: z.array(VideoAttachmentSchema.extend({ sortOrder: z.number() })),
      programRating: ProgramRatingResponseSchema,
      programFormat: z.object({
        programFormatId: IDSchema,
        displayName: z.string(),
        slug: ProgramFormatSchema,
      }),
      embedding: EmbeddingVectorSchema,
      artwork: ImageAttachmentResponseWithCroppedUrlSchema,
      background: ImageAttachmentResponseWithCroppedUrlSchema,
    }),
  ),
  page: PageResponseSchema,
})

export const PostProgramsResponseSchema = z.object({
  data: ProgramSchema,
})

function programIdSchema(message: string) {
  return z
    .union([z.string().min(1, message), z.number()], { message })
    .transform(Number)
    .pipe(IDSchema.positive(message))
}

const ProgramVideoAttachmentsBodySchema = z
  .array(
    z.object({
      videoAttachmentId: IDSchema,
      url: VideoAttachmentSchema.shape.url,
      mimeType: VideoAttachmentSchema.shape.mimeType,
      name: z.string().optional(),
      sortOrder: z.number().optional(),
      previewPosterUrl: z.string().url().nullable().optional(),
      previewPosterImage: ImageAttachmentFormSchema.nullable().optional(),
      previewPosterImageAttachment: ImageAttachmentWithRequiredCropSchema.nullable().optional(),
    }),
  )
  .transform(videoAttachments =>
    videoAttachments.map((videoAttachment, index) => {
      const previewPosterImageAttachment =
        videoAttachment.previewPosterImage !== undefined
          ? videoAttachment.previewPosterImage
            ? {
                url: videoAttachment.previewPosterImage.url,
                cropX: videoAttachment.previewPosterImage.cropX,
                cropY: videoAttachment.previewPosterImage.cropY,
                cropWidth: videoAttachment.previewPosterImage.cropWidth,
                cropHeight: videoAttachment.previewPosterImage.cropHeight,
              }
            : null
          : videoAttachment.previewPosterImageAttachment

      return {
        videoAttachmentId: videoAttachment.videoAttachmentId,
        sortOrder: videoAttachment.sortOrder ?? index + 1,
        ...(videoAttachment.previewPosterUrl !== undefined
          ? { previewPosterUrl: videoAttachment.previewPosterUrl }
          : undefined),
        ...(previewPosterImageAttachment !== undefined ? { previewPosterImageAttachment } : undefined),
      }
    }),
  )

export const PostProgramsBodySchema = z.object({
  program: BaseProgramSchema.extend({
    networkId: programIdSchema('Please select a network'),
    programRatingId: programIdSchema('Please select a program rating'),
    programFormatId: programIdSchema('Please select a program type'),
  }),
  programGenreIds: z
    .array(programIdSchema('Please select a genre'), {
      message: 'Please select a genre',
    })
    .min(1, 'Please select a genre')
    .pipe(RequiredIDsSchema),
  videoAttachments: ProgramVideoAttachmentsBodySchema.default([]),
  imageAttachments: z.object({
    artwork: ImageAttachmentFormSchema,
    background: ImageAttachmentFormSchema,
  }),
})

export const PatchProgramsBodySchema = z.object({
  programId: IDSchema,
  updates: BaseProgramSchema.partial().extend({
    networkId: programIdSchema('Please select a network').optional(),
    programRatingId: programIdSchema('Please select a program rating').optional(),
    programFormatId: programIdSchema('Please select a program type').optional(),
  }),
  programGenreIds: z
    .array(programIdSchema('Please select a genre'), {
      message: 'Please select a genre',
    })
    .min(1, 'Please select a genre')
    .pipe(RequiredIDsSchema)
    .optional(),
  videoAttachments: ProgramVideoAttachmentsBodySchema.optional(),
  imageAttachments: z
    .object({
      artwork: ImageAttachmentFormSchema.optional(),
      background: ImageAttachmentFormSchema.optional(),
    })
    .optional(),
})

export const PatchProgramsResponseSchema = z.object({
  data: ProgramSchema,
})

export const DeleteProgramBodySchema = z.object({
  programId: z.number(),
})

export const DeleteProgramResponseSchema = z.object({
  programId: NaturalNumberSchema,
})

export const GetProgramsRequestSchema = z.object({
  query: z.custom<z.output<typeof GetProgramsQuerySchema>>(),
  principal: T.KindSchema(['publisher', 'advertiser']).and(z.union([A.UserSchema, A.ServiceSchema])),
})

export const PostProgramsRequestSchema = z.object({
  body: PostProgramsBodySchema,
  principal: T.KindSchema('publisher').and(z.union([A.UserSchema, A.ServiceSchema])),
})

export const PatchProgramsRequestSchema = z.object({
  body: PatchProgramsBodySchema,
  principal: T.KindSchema('publisher').and(A.UserSchema),
})

export const DeleteProgramRequestSchema = z.object({
  body: z.custom<z.output<typeof DeleteProgramBodySchema>>(),
  principal: T.KindSchema('publisher').and(A.UserSchema),
})
